// src/services/classesService.js
import apiClient from './api.js';

class ClassesService {
  // Get all classes with filtering and pagination
  async getClasses(params = {}) {
    try {
      const response = await apiClient.get('/classes', params);
      // Transform the classes array
      if (response.classes && Array.isArray(response.classes)) {
        response.classes = this.transformClassesFromBackend(response.classes);
      }
      return response;
    } catch (error) {
      console.error('Error fetching classes:', error);
      throw error;
    }
  }

  // Get single class by ID
  async getClass(id) {
    try {
      const response = await apiClient.get(`/classes/${id}`);
      console.log('ClassesService - getClass raw response:', response);
      const transformed = this.transformClassFromBackend(response);
      console.log('ClassesService - getClass transformed:', transformed);
      return transformed;
    } catch (error) {
      console.error('Error fetching class:', error);
      throw error;
    }
  }

  // Create new class
  async createClass(classData) {
    try {
      // Transform frontend data format to backend format
      const backendData = {
        title: classData.title,
        instructor: classData.instructor,
        category: classData.category,
        skillLevel: classData.skillLevel,
        frequency: classData.frequency,
        dayOfWeek: classData.dayOfWeek,
        startMonth: classData.startMonth,
        endMonth: classData.endMonth,
        oneTimeDate: classData.oneTimeDate,
        customDates: classData.customDates,
        maxCapacity: parseInt(classData.maxCapacity) || 20,
        startDate: classData.startDate || null,
        endDate: classData.endDate || null,
        startTime: classData.startTime,
        duration: parseInt(classData.duration),
        location: classData.location,
        price: parseFloat(classData.price) || 0,
        description: classData.description,
        tags: Array.isArray(classData.tags) ? classData.tags : [],
        specialInstructions: classData.specialInstructions,
        minimumAge: classData.minimumAge && classData.minimumAge !== '' ? parseInt(classData.minimumAge) : null,
        prerequisiteClassIds: classData.prerequisiteClassIds || [],
        projectId: classData.projectId,
        materialsFee: classData.materialsFee ? parseFloat(classData.materialsFee) : 0,
        materialsDescription: classData.materialsDescription || '',
        waitlistEnabled: classData.waitlistEnabled || false,
        followupClassIds: classData.followupClassIds || [],
        followupDescription: classData.followupDescription || ''
      };

      console.log('ClassesService - Sending to backend:', backendData);

      const response = await apiClient.post('/classes', backendData);
      console.log('ClassesService - Raw response from backend:', response);
      
      return this.transformClassFromBackend(response);
    } catch (error) {
      console.error('Error creating class:', error);
      throw error;
    }
  }

  // Update existing class
  async updateClass(id, classData) {
    try {
      // Transform frontend data format to backend format
      const backendData = {
        title: classData.title,
        instructor: classData.instructor,
        category: classData.category,
        skillLevel: classData.skillLevel,
        frequency: classData.frequency,
        dayOfWeek: classData.dayOfWeek,
        startMonth: classData.startMonth,
        endMonth: classData.endMonth,
        oneTimeDate: classData.oneTimeDate,
        customDates: classData.customDates,
        maxCapacity: parseInt(classData.maxCapacity) || 20,
        startDate: classData.startDate || null,
        endDate: classData.endDate || null,
        startTime: classData.startTime,
        duration: parseInt(classData.duration),
        location: classData.location,
        price: parseFloat(classData.price) || 0,
        description: classData.description,
        tags: Array.isArray(classData.tags) ? classData.tags : [],
        specialInstructions: classData.specialInstructions,
        minimumAge: classData.minimumAge && classData.minimumAge !== '' ? parseInt(classData.minimumAge) : null,
        prerequisiteClassIds: classData.prerequisiteClassIds || [],
        projectId: classData.projectId,
        materialsFee: classData.materialsFee ? parseFloat(classData.materialsFee) : 0,
        materialsDescription: classData.materialsDescription || '',
        waitlistEnabled: classData.waitlistEnabled || false,
        followupClassIds: classData.followupClassIds || [],
        followupDescription: classData.followupDescription || ''
      };
      

      const response = await apiClient.put(`/classes/${id}`, backendData);
      return this.transformClassFromBackend(response);
    } catch (error) {
      console.error('Error updating class:', error);
      throw error;
    }
  }

  // Delete class
  async deleteClass(id) {
    try {
      const response = await apiClient.delete(`/classes/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting class:', error);
      throw error;
    }
  }

  // Bulk delete classes
  async deleteClasses(ids) {
    try {
      const response = await apiClient.delete('/classes', { ids });
      return response;
    } catch (error) {
      console.error('Error bulk deleting classes:', error);
      throw error;
    }
  }

  // Get class statistics
  async getClassStats() {
    try {
      const response = await apiClient.get('/classes/stats/overview');
      return response;
    } catch (error) {
      console.error('Error fetching class stats:', error);
      throw error;
    }
  }

  // Transform backend class data to frontend format
  transformClassFromBackend(backendClass) {
    console.log('ClassesService - Transforming class from backend:', backendClass);
    
    const transformed = {
      id: backendClass.id,
      title: backendClass.title,
      instructor: backendClass.instructor,
      category: backendClass.category,
      skillLevel: backendClass.skill_level || backendClass.skillLevel,
      frequency: backendClass.frequency,
      dayOfWeek: backendClass.day_of_week || backendClass.dayOfWeek || '',
      startMonth: backendClass.start_month || backendClass.startMonth || '',
      endMonth: backendClass.end_month || backendClass.endMonth || '',
      oneTimeDate: backendClass.one_time_date || backendClass.oneTimeDate || '',
      customDates: backendClass.custom_dates || backendClass.customDates || [],
      maxCapacity: backendClass.max_capacity || backendClass.maxCapacity,
      currentEnrollment: backendClass.current_enrollment || backendClass.currentEnrollment,
      startDate: backendClass.start_date ? backendClass.start_date.split('T')[0] : (backendClass.startDate || null),
      endDate: backendClass.end_date ? backendClass.end_date.split('T')[0] : (backendClass.endDate || null),
      startTime: backendClass.start_time || backendClass.startTime,
      duration: backendClass.duration,
      location: backendClass.location,
      price: parseFloat(backendClass.price || 0),
      materialsFee: parseFloat(backendClass.materials_fee || backendClass.materialsFee || 0),
      materialsDescription: backendClass.materials_description || backendClass.materialsDescription || '',
      description: backendClass.description,
      tags: backendClass.tags || [],
      specialInstructions: backendClass.special_instructions || backendClass.specialInstructions,
      minimumAge: backendClass.minimum_age || backendClass.minimumAge,
      prerequisiteClassIds: backendClass.prerequisite_class_ids || backendClass.prerequisiteClassIds || [],
      waitlistEnabled: backendClass.waitlist_enabled !== undefined ? backendClass.waitlist_enabled : (backendClass.waitlistEnabled !== undefined ? backendClass.waitlistEnabled : false),
      followupClassIds: backendClass.followup_class_ids || backendClass.followupClassIds || [],
      followupDescription: backendClass.followup_description || backendClass.followupDescription || '',
      status: backendClass.status,
      availableSpots: backendClass.available_spots || backendClass.availableSpots,
      dateCreated: backendClass.created_at || backendClass.createdAt,
      lastModified: backendClass.updated_at || backendClass.updatedAt,

      // Project information - handle both snake_case (from backend) and camelCase
      projectId: backendClass.project_id || backendClass.projectId,
      projectTitle: backendClass.project_title || backendClass.projectTitle,
      projectDescription: backendClass.project_description || backendClass.projectDescription,
      projectDifficulty: backendClass.project_difficulty || backendClass.projectDifficulty,
      projectDuration: backendClass.project_duration || backendClass.projectDuration,
      projectCategory: backendClass.project_category || backendClass.projectCategory,
      projectMaterials: backendClass.project_materials || backendClass.projectMaterials,
      projectInstructions: backendClass.project_instructions || backendClass.projectInstructions,
      projectImageUrl: backendClass.project_image_url || backendClass.projectImageUrl,
      projectMainImageUrl: backendClass.project_main_image_url || backendClass.projectMainImageUrl,

      // Session date range from class_sessions table
      firstSessionDate: backendClass.first_session_date || backendClass.firstSessionDate,
      lastSessionDate: backendClass.last_session_date || backendClass.lastSessionDate
    };

    console.log('ClassesService - Transformed class:', transformed);
    console.log('ClassesService - Project fields in transformed:', {
      projectId: transformed.projectId,
      projectTitle: transformed.projectTitle,
      projectDescription: transformed.projectDescription,
      projectDifficulty: transformed.projectDifficulty
    });

    return transformed;
  }

  // Transform multiple classes from backend
  transformClassesFromBackend(backendClasses) {
    console.log('ClassesService - Transforming multiple classes:', backendClasses.length);
    return backendClasses.map(cls => this.transformClassFromBackend(cls));
  }
}

// Create singleton instance
const classesService = new ClassesService();

export default classesService;